import { useState, useRef } from 'react'
import type { DocumentInput, DocumentResult, SavedDocument } from '@creanizer/types'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription, AlertTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { AlertCircle, Save, Download, FileText, Settings } from 'lucide-react'
import { useDocumentStore } from '../../../../stores/documentStore'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useSocietyStore } from '../../../../stores/societyStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useSkillStore } from '../../../../stores/skillStore'
import { useBuildingStore } from '../../../../stores/buildingStore'
import { 
  generateWorldContextText, 
  generateCharacterContext,
  generateEnemyContext,
  generateCommunityContext,
  generateSocietyContext,
  generateItemContext
} from '@creanizer/utils'
import { DocumentFormEnhanced } from './DocumentFormEnhanced'
import { DocumentResult as DocumentResultDisplay } from './DocumentResult'
import { SavedDocumentsList } from './SavedDocumentsList'
import { getDefaultStyle } from './utils/visualStyles'
import { DocumentExporter } from './DocumentExporter'
import { DocumentImporter } from './DocumentImporter'
import { parseDocumentResponse } from './utils/documentParser'

export function DocumentGenerator() {
  const { isApiKeySet } = useSettingsStore()
  const { documents, addDocument, addBatchDocuments, removeDocument, updateDocument } = useDocumentStore()
  const { currentWorld, savedWorlds } = useWorldBuilderStore()
  const { characters } = useCharacterStore()
  const { enemies } = useEnemyStore()
  const { savedCommunities: communities } = useCommunityStore()
  const { savedSocieties: societies } = useSocietyStore()
  const { items } = useItemStore()
  const { skills } = useSkillStore()
  const { buildings } = useBuildingStore()
  const generator = useGenerator('document')
  const streamGenerator = useStreamGenerator('document')
  
  const [activeTab, setActiveTab] = useState('generate')
  const [input, setInput] = useState<DocumentInput>({
    documentType: 'newspaper',
    genre: 'fantasy',
    purpose: '',
    // 世界観連携
    worldContext: currentWorld ? {
      worldId: currentWorld.id,
      setting: currentWorld.result.summary,
      era: currentWorld.result.details.era?.name,
      culture: currentWorld.result.details.cultures?.map(c => c.description).join(', ')
    } : undefined
  })
  
  const [results, setResults] = useState<DocumentResult[]>([])
  const [error, setError] = useState<string>('')
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null)
  const [useStreaming, setUseStreaming] = useState<boolean>(true)
  const [useProMode, setUseProMode] = useState<boolean>(false)
  const [useGemini3Mode, setUseGemini3Mode] = useState<boolean>(false)
  const [useGemini3FlashMode, setUseGemini3FlashMode] = useState<boolean>(false)
  const [useGemini3FlashLiteMode, setUseGemini3FlashLiteMode] = useState<boolean>(false)
  const [streamingContent, setStreamingContent] = useState<string>('')
  const resultRef = useRef<HTMLDivElement>(null)
  
  // 文献生成
  const handleGenerate = async () => {
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません。設定ページから設定してください。')
      return
    }
    
    setError('')
    setResults([])
    setBatchProgress(null)
    
    try {
      // ストリーミングモードの場合
      if (useStreaming) {
        await streamGenerator.generateStream({ ...input, useProMode, useGemini3Mode, useGemini3FlashMode, useGemini3FlashLiteMode }, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
            setStreamingContent(chunk)
          },
          onComplete: (result) => {
            const parsedResult = parseDocumentResponse(result, input.documentType)
            setResults([parsedResult])
            setStreamingContent('')
          },
          onError: (error) => {
            setError(error.message)
            setStreamingContent('')
          }
        })
      } else {
        const response = await generator.mutateAsync({ ...input, useProMode, useGemini3Mode, useGemini3FlashMode, useGemini3FlashLiteMode })
        const result = parseDocumentResponse(response.result, input.documentType)
        setResults([result])
      }
      
      // 結果へ自動スクロール
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } catch (err: any) {
      setError(err.message || '文献の生成に失敗しました')
    }
  }
  
  // 文献保存
  const handleSave = (result: DocumentResult, index?: number) => {
    console.log('[handleSave] Saving with input:', input)
    console.log('[handleSave] input.characterIds:', input.characterIds)
    console.log('[handleSave] input.continuationFrom:', input.continuationFrom)
    if (input.continuationFrom?.previousContents) {
      console.log('[handleSave] previousContents count:', input.continuationFrom.previousContents.length)
      console.log('[handleSave] previousContents types:', input.continuationFrom.previousContents.map(c => c.type))
    }
    
    const savedDoc: SavedDocument = {
      id: `doc_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      title: generateTitle(result),
      input,
      result,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      worldId: input.worldContext?.worldId || currentWorld?.id,
      characterIds: input.characterIds,
      eventIds: input.eventIds
    }
    
    console.log('[handleSave] Saved doc with characterIds:', savedDoc.characterIds)
    console.log('[handleSave] Saved doc.input.continuationFrom:', savedDoc.input.continuationFrom)
    addDocument(savedDoc)
  }
  
  // 全保存
  const handleSaveAll = () => {
    if (results.length === 0) return
    
    console.log('[handleSaveAll] Saving all with input.continuationFrom:', input.continuationFrom)
    
    const docsToSave = results.map((result, index) => ({
      id: `doc_${Date.now() + index}_${Math.random().toString(36).slice(2, 11)}`,
      title: generateTitle(result),
      input,
      result,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      worldId: input.worldContext?.worldId || currentWorld?.id,
      characterIds: input.characterIds,
      eventIds: input.eventIds
    }))
    
    addBatchDocuments(docsToSave)
  }
  
  // タイトル生成
  const generateTitle = (result: DocumentResult): string => {
    const now = new Date()
    const dateStr = now.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })
    const timeStr = now.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
    
    switch(result.type) {
      case 'newspaper':
        return (result as any).headline || `新聞記事 - ${dateStr}`
      case 'sns_twitter':
        const posts = (result as any).posts || []
        if (posts[0]?.text) {
          return `Twitter - ${posts[0].text.slice(0, 30)}${posts[0].text.length > 30 ? '...' : ''}`
        }
        return `Twitter投稿 - ${dateStr} ${timeStr}`
      case 'diary':
        return `日記 - ${(result as any).date || dateStr}`
      case 'report':
        return (result as any).title || `報告書 - ${dateStr}`
      case 'blog':
        return (result as any).title || `ブログ記事 - ${dateStr}`
      case 'email':
        return (result as any).subject || `メール - ${dateStr} ${timeStr}`
      case 'letter':
        const senderName = (result as any).sender?.name
        return senderName ? `手紙 - ${senderName}` : `手紙 - ${dateStr}`
      case 'memo':
        return (result as any).title || `メモ - ${dateStr} ${timeStr}`
      case 'medical':
        const patientId = (result as any).header?.patientId
        return patientId ? `医療記録 - ${patientId}` : `医療記録 - ${dateStr}`
      case 'police':
        const caseNumber = (result as any).header?.caseNumber
        return caseNumber ? `警察調書 - ${caseNumber}` : `警察調書 - ${dateStr}`
      case 'academic':
        return (result as any).title || `学術論文 - ${dateStr}`
      case 'government':
        return (result as any).title || `政府公式文書 - ${dateStr}`
      case 'line':
        const lineResult = result as any
        // タイトルがある場合はそれを使用
        if (lineResult.title) {
          return lineResult.title
        }
        // 参加者名を使用
        const participants = lineResult.participants || []
        if (participants.length > 0) {
          const names = participants.slice(0, 2).map((p: any) => p.name).join('と')
          const suffix = participants.length > 2 ? '他' : ''
          return `LINE - ${names}${suffix} (${dateStr} ${timeStr})`
        }
        // 最初のメッセージを使用
        const firstMessage = lineResult.messages?.[0]
        if (firstMessage?.content) {
          const preview = firstMessage.content.slice(0, 20)
          return `LINE - ${preview}${firstMessage.content.length > 20 ? '...' : ''} (${timeStr})`
        }
        return `LINE会話 - ${dateStr} ${timeStr}`
      case 'magazine':
        return (result as any).title || `雑誌記事 - ${dateStr}`
      case 'history':
        return (result as any).title || `歴史書 - ${dateStr}`
      case 'custom':
        return (result as any).title || `カスタム文献 - ${dateStr} ${timeStr}`
      default:
        // 不明なタイプの場合もタイプ名と日時を含める
        const typeName = (result as any).type || '文献'
        return `${typeName} - ${dateStr} ${timeStr}`
    }
  }
  
  // 保存済み文献の削除
  const handleDelete = (id: string) => {
    removeDocument(id)
  }
  
  // 保存済み文献の読み込み
  const handleLoad = (doc: SavedDocument) => {
    console.log('[handleLoad] Loading doc:', doc)
    console.log('[handleLoad] doc.input.continuationFrom:', doc.input?.continuationFrom)
    if (doc.input?.continuationFrom?.previousContents) {
      console.log('[handleLoad] previousContents count:', doc.input.continuationFrom.previousContents.length)
    }
    
    // 入力を復元
    // ただし、各種コンテキストは文字列で再生成するため、ここでは削除
    const { 
      characterContext, 
      enemyContext,
      communityContext,
      societyContext,
      itemContext,
      ...inputWithoutContexts 
    } = doc.input || {}
    const restoredInput: DocumentInput = { 
      documentType: doc.input?.documentType || 'custom',
      genre: doc.input?.genre || 'fantasy',
      purpose: doc.input?.purpose || '文献の読み込み',
      ...inputWithoutContexts 
    }
    
    // 世界観コンテキストを復元
    if (doc.worldId) {
      const world = savedWorlds.find(w => w.id === doc.worldId)
      if (world) {
        restoredInput.worldContext = {
          worldId: world.id,
          setting: world.result.summary,
          era: world.result.details.era?.name,
          culture: world.result.details.cultures?.map(c => c.description).join(', ')
        }
        restoredInput.worldContextText = generateWorldContextText(world.result)
      }
    }
    
    // キャラクターコンテキストを復元
    if (doc.characterIds && doc.characterIds.length > 0) {
      const selectedChars = characters.filter(c => doc.characterIds?.includes(c.id))
      if (selectedChars.length > 0) {
        (restoredInput as any).characterContext = selectedChars.map(c => generateCharacterContext(c)).join('\n\n')
        restoredInput.characterIds = doc.characterIds
      }
    }
    
    // エネミーコンテキストを復元
    if (restoredInput.enemyIds && restoredInput.enemyIds.length > 0) {
      const selectedEnemies = enemies.filter(e => restoredInput.enemyIds?.includes(e.id))
      if (selectedEnemies.length > 0) {
        restoredInput.enemyContext = selectedEnemies.map(e => generateEnemyContext(e)).join('\n\n')
      }
    }
    
    // コミュニティコンテキストを復元
    if (restoredInput.communityIds && restoredInput.communityIds.length > 0) {
      const selectedCommunities = communities.filter(c => restoredInput.communityIds?.includes(c.id))
      if (selectedCommunities.length > 0) {
        restoredInput.communityContext = selectedCommunities.map(c => generateCommunityContext(c)).join('\n\n')
      }
    }
    
    // 社会コンテキストを復元
    if (restoredInput.societyIds && restoredInput.societyIds.length > 0) {
      const selectedSocieties = societies.filter(s => restoredInput.societyIds?.includes(s.id))
      if (selectedSocieties.length > 0) {
        restoredInput.societyContext = selectedSocieties.map(s => generateSocietyContext(s)).join('\n\n')
      }
    }
    
    // アイテムコンテキストを復元
    if (restoredInput.itemIds && restoredInput.itemIds.length > 0) {
      const selectedItems = items.filter(i => restoredInput.itemIds?.includes(i.id))
      if (selectedItems.length > 0) {
        restoredInput.itemContext = selectedItems.map(i => generateItemContext(i)).join('\n\n')
      }
    }
    
    setInput(restoredInput)
    setResults([doc.result])
    setActiveTab('generate')
  }
  
  // 文献の続きを作成（異なる文献タイプも可能）
  const handleContinue = (doc: SavedDocument, targetType?: string) => {
    console.log('[handleContinue] Starting with doc:', doc)
    console.log('[handleContinue] doc.input:', doc.input)
    console.log('[handleContinue] doc.characterIds:', doc.characterIds)
    console.log('[handleContinue] doc.input.characterIds:', doc.input.characterIds)
    console.log('[handleContinue] targetType:', targetType)
    console.log('[handleContinue] doc.result.type:', doc.result.type)
    
    // 続きの文献タイプを決定（指定がなければ同じタイプ、指定があれば変更）
    const newDocumentType = targetType || doc.result.type
    const isSameType = newDocumentType === doc.result.type
    console.log('[handleContinue] newDocumentType:', newDocumentType, 'isSameType:', isSameType)
    
    // 続き作成時は、元の入力を復元（キャラクター選択も含む）
    // ただし、各種コンテキストは文字列で再生成するため、ここでは削除
    // continuationFromは保持する（文献チェーン情報を含むため）
    console.log('[handleContinue] doc.input before destructuring:', doc.input)
    console.log('[handleContinue] doc.input.continuationFrom before destructuring:', doc.input?.continuationFrom)
    
    const { 
      characterContext, 
      enemyContext,
      communityContext,
      societyContext,
      itemContext,
      ...inputWithoutContexts 
    } = doc.input || {}
    const restoredInput: Partial<DocumentInput> = { 
      genre: doc.input?.genre || 'fantasy',  // デフォルト値を設定
      purpose: doc.input?.purpose || '続きの生成',  // デフォルト値を設定
      ...inputWithoutContexts 
    }
    console.log('[handleContinue] restoredInput after destructuring:', restoredInput)
    console.log('[handleContinue] restoredInput.continuationFrom after destructuring:', restoredInput.continuationFrom)
    
    // キャラクターIDを確実に復元（doc.characterIds優先、なければdoc.input.characterIds）
    const characterIdsToRestore = doc.characterIds || doc.input.characterIds || []
    if (characterIdsToRestore.length > 0) {
      restoredInput.characterIds = characterIdsToRestore
      console.log('[handleContinue] Setting characterIds to restoredInput:', characterIdsToRestore)
    }
    
    console.log('[handleContinue] restoredInput after characterIds restoration:', restoredInput)
    
    // 世界観コンテキストを復元
    if (doc.worldId) {
      const world = savedWorlds.find(w => w.id === doc.worldId)
      if (world) {
        restoredInput.worldContext = {
          worldId: world.id,
          setting: world.result.summary,
          era: world.result.details.era?.name,
          culture: world.result.details.cultures?.map(c => c.description).join(', ')
        }
        restoredInput.worldContextText = generateWorldContextText(world.result)
      }
    }
    
    // キャラクターコンテキストを復元（restoredInput.characterIdsを使用）
    if (restoredInput.characterIds && restoredInput.characterIds.length > 0) {
      const selectedChars = characters.filter(c => restoredInput.characterIds?.includes(c.id))
      if (selectedChars.length > 0) {
        restoredInput.characterContext = selectedChars.map(c => generateCharacterContext(c)).join('\n\n')
        console.log('[handleContinue] Character context restored for IDs:', restoredInput.characterIds)
      }
    }
    
    // エネミーコンテキストを復元
    if (restoredInput.enemyIds && restoredInput.enemyIds.length > 0) {
      const selectedEnemies = enemies.filter(e => restoredInput.enemyIds?.includes(e.id))
      if (selectedEnemies.length > 0) {
        restoredInput.enemyContext = selectedEnemies.map(e => generateEnemyContext(e)).join('\n\n')
      }
    }
    
    // コミュニティコンテキストを復元
    if (restoredInput.communityIds && restoredInput.communityIds.length > 0) {
      const selectedCommunities = communities.filter(c => restoredInput.communityIds?.includes(c.id))
      if (selectedCommunities.length > 0) {
        restoredInput.communityContext = selectedCommunities.map(c => generateCommunityContext(c)).join('\n\n')
      }
    }
    
    // 社会コンテキストを復元
    if (restoredInput.societyIds && restoredInput.societyIds.length > 0) {
      const selectedSocieties = societies.filter(s => restoredInput.societyIds?.includes(s.id))
      if (selectedSocieties.length > 0) {
        restoredInput.societyContext = selectedSocieties.map(s => generateSocietyContext(s)).join('\n\n')
      }
    }
    
    // アイテムコンテキストを復元
    if (restoredInput.itemIds && restoredInput.itemIds.length > 0) {
      const selectedItems = items.filter(i => restoredInput.itemIds?.includes(i.id))
      if (selectedItems.length > 0) {
        restoredInput.itemContext = selectedItems.map(i => generateItemContext(i)).join('\n\n')
      }
    }
    
    // 文献タイプ間の関係性を定義（汎用的な説明）
    const getContinuationDescription = (fromType: string, toType: string) => {
      if (fromType === toType) {
        // 同じタイプの場合
        const sameTypeDescriptions: Record<string, string> = {
          'diary': '翌日の日記',
          'sns_twitter': '続きの投稿',
          'blog': '続編の記事',
          'memo': '追加のメモ',
          'newspaper': '続報',
          'report': '追加報告',
          'letter': '返信',
          'email': '返信メール',
          'medical': '経過記録',
          'police': '追加調書',
          'academic': '続編論文',
          'government': '追加文書',
          'magazine': '続編記事',
          'line': '会話の続き',
          'history': '続きの記述',
          'custom': '続きの文書'
        }
        return sameTypeDescriptions[fromType] || '続きの文献'
      } else {
        // 異なるタイプの場合（汎用的な関係性）
        return `${getDocumentTypeLabel(fromType)}を受けての${getDocumentTypeLabel(toType)}`
      }
    }
    
    const continuationDescription = getContinuationDescription(doc.result.type, newDocumentType)
    
    // 汎用的な指示を作成
    let additionalInstructions = ''
    if (isSameType) {
      additionalInstructions = `前回の${getDocumentTypeLabel(doc.result.type)}の続きとして、自然な時間経過と展開を含めて生成してください。`
    } else {
      additionalInstructions = `前回の${getDocumentTypeLabel(doc.result.type)}の内容を踏まえて、それに対する反応・感想・報道・議論などを${getDocumentTypeLabel(newDocumentType)}として生成してください。
      
      例：
      - 新聞記事→SNS: 記事に対する人々の反応
      - ブログ→メール: 記事を読んだ感想を友人に送る
      - 報告書→新聞: 報告書の内容を報道
      - SNS→日記: SNSでの出来事を振り返る
      - 手紙→日記: 手紙を受け取った感想
      
      前の文献の重要な要素を適切に参照し、${getDocumentTypeLabel(newDocumentType)}として自然な形で表現してください。`
    }
    
    // 過去の文献チェーンを構築
    console.log('[handleContinue] Checking restoredInput.continuationFrom:', restoredInput.continuationFrom)
    console.log('[handleContinue] restoredInput.continuationFrom.previousContents:', restoredInput.continuationFrom?.previousContents)
    
    let previousContents = []
    if (restoredInput.continuationFrom?.previousContents && restoredInput.continuationFrom.previousContents.length > 0) {
      // 既存の文献チェーンがある場合はそれを引き継ぐ
      previousContents = [...restoredInput.continuationFrom.previousContents]
      console.log('[handleContinue] Found existing chain with', previousContents.length, 'documents')
      console.log('[handleContinue] Existing chain types:', previousContents.map(c => c.type))
    } else if (restoredInput.continuationFrom?.previousContent) {
      // 単一の前回文献がある場合
      previousContents = [restoredInput.continuationFrom.previousContent]
      console.log('[handleContinue] Found single previous document of type:', restoredInput.continuationFrom.previousContent.type)
    } else {
      console.log('[handleContinue] No previous documents found in restoredInput')
    }
    
    // 今回の文献を文献チェーンに追加
    console.log('[handleContinue] Adding current doc of type:', doc.result.type)
    previousContents.push(doc.result)
    console.log('[handleContinue] Total documents in chain:', previousContents.length)
    console.log('[handleContinue] Final chain types in order:', previousContents.map(c => c.type))
    
    // 入力を設定
    const finalInput: DocumentInput = {
      documentType: newDocumentType,  // 新しい文献タイプを設定
      genre: restoredInput.genre || 'fantasy',  // genreを確実に含める
      purpose: restoredInput.purpose || '続きの生成',  // purposeを確実に含める
      ...restoredInput,  // 復元されたコンテキストを含む入力を使用
      continuationFrom: {
        previousDocumentId: doc.id,
        previousContent: doc.result,  // 互換性のため維持
        previousContents: previousContents,  // 文献チェーン全体
        continuationType: isSameType ? 'sequel' : 'reaction',
        timeGap: continuationDescription,
        originalType: doc.result.type  // 元の文献タイプを記録
      },
      additionalSettings: additionalInstructions,
      // 異なるタイプの場合、ビジュアルスタイルをリセット
      visualStyle: !isSameType ? { theme: getDefaultStyle(newDocumentType) } : restoredInput.visualStyle
    }
    
    console.log('[handleContinue] Final input to set:', finalInput)
    console.log('[handleContinue] Final input characterIds:', finalInput.characterIds)
    console.log('[handleContinue] Final input characterContext:', (finalInput as any).characterContext)
    
    setInput(finalInput)
    setResults([])
    setActiveTab('generate')
  }
  
  // 文献タイプのラベル取得
  const getDocumentTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'newspaper': '新聞記事',
      'sns_twitter': 'Twitter/X投稿',
      'diary': '日記',
      'report': '報告書',
      'blog': 'ブログ',
      'email': 'メール',
      'letter': '手紙',
      'memo': 'メモ',
      'medical': '医療記録',
      'police': '警察調書',
      'academic': '学術論文',
      'government': '政府文書',
      'magazine': '雑誌記事',
      'line': 'LINE会話',
      'history': '歴史書',
      'custom': 'カスタム文献'
    }
    return labels[type] || type
  }
  
  // 保存済みチェック
  const isSaved = (result: DocumentResult) => {
    if (!result) return false
    return documents.some(doc => 
      doc?.result?.type === result.type &&
      generateTitle(doc.result) === generateTitle(result)
    )
  }
  
  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-5 w-5" />
          <h2 className="text-xl font-semibold">文献ジェネレータ</h2>
        </div>
        <p className="text-muted-foreground mb-4">
          新聞記事、SNS投稿、日記、報告書など、様々な文献を生成します
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="generate">生成</TabsTrigger>
          <TabsTrigger value="saved">
            保存済み ({documents.length}/200)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-6">
          {/* 進捗表示 */}
          {batchProgress && (
            <div className="card">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>生成中...</span>
                  <span>{batchProgress.current}/{batchProgress.total}</span>
                </div>
                <Progress
                  value={(batchProgress.current / batchProgress.total) * 100}
                  className="w-full"
                />
              </div>
            </div>
          )}
          
          {/* 入力フォーム */}
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">生成設定</h3>
            
            {/* 生成モード設定 */}
            <div className="mb-4 p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Settings className="h-4 w-4" />
                <label className="text-sm font-medium">生成モード</label>
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    id="useProMode"
                    type="checkbox"
                    checked={useProMode}
                    onChange={(e) => {
                      setUseProMode(e.target.checked)
                      if (e.target.checked) { setUseGemini3Mode(false); setUseGemini3FlashLiteMode(false) }
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">Pro モード（より高品質な生成 - Gemini 2.5 Pro使用）</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    id="useGemini3Mode"
                    type="checkbox"
                    checked={useGemini3Mode}
                    onChange={(e) => {
                      setUseGemini3Mode(e.target.checked)
                      if (e.target.checked) {
                        setUseProMode(false)
                        setUseGemini3FlashMode(false)
                        setUseGemini3FlashLiteMode(false)
                      }
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">Gemini 3 モード（有料APIのみ・試験版）</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    id="useGemini3FlashMode"
                    type="checkbox"
                    checked={useGemini3FlashMode}
                    onChange={(e) => {
                      setUseGemini3FlashMode(e.target.checked)
                      if (e.target.checked) {
                        setUseProMode(false)
                        setUseGemini3Mode(false)
                        setUseGemini3FlashLiteMode(false)
                      }
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">Gemini 3 Flash モード（有料APIのみ・試験版・高速）</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    id="useGemini3FlashLiteMode"
                    type="checkbox"
                    checked={useGemini3FlashLiteMode}
                    onChange={(e) => {
                      setUseGemini3FlashLiteMode(e.target.checked)
                      if (e.target.checked) {
                        setUseProMode(false)
                        setUseGemini3Mode(false)
                        setUseGemini3FlashMode(false)
                      }
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">Gemini 3.1 Flash Lite モード（有料APIのみ・試験版・超軽量）</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={useStreaming}
                    onChange={(e) => setUseStreaming(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">ストリーミングモード（リアルタイム生成）</span>
                </label>
              </div>
            </div>
            
            <DocumentFormEnhanced
              input={input}
              onChange={setInput}
              onGenerate={handleGenerate}
              isGenerating={generator.isPending || streamGenerator.isStreaming}
            />
          </div>
          
          {/* ストリーミング表示 */}
          {streamGenerator.isStreaming && streamingContent && (
            <div className="card">
              <div className="flex items-center gap-2 mb-4">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <h3 className="text-lg font-semibold">生成中...</h3>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 max-h-96 overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap font-mono">
                  {streamingContent}
                </pre>
              </div>
            </div>
          )}
          
          {/* 結果表示 */}
          {results.length > 0 && (
            <div className="card" ref={resultRef}>
              <div className="flex flex-row items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  生成結果
                  {results.length > 1 && ` (${results.length}件)`}
                </h3>
                {results.length > 1 && (
                  <Button
                    onClick={handleSaveAll}
                    className="flex items-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    すべて保存
                  </Button>
                )}
              </div>
              
              <div className="space-y-4">
                {results.map((result, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">
                        {generateTitle(result)}
                      </h4>
                      <div className="flex gap-2">
                        {!isSaved(result) && (
                          <Button
                            onClick={() => handleSave(result, index)}
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2"
                          >
                            <Save className="w-4 h-4" />
                            保存
                          </Button>
                        )}
                        <DocumentExporter
                          document={result}
                          trigger={
                            <Button variant="outline" size="sm" className="flex items-center gap-2">
                              <Download className="w-4 h-4" />
                              エクスポート
                            </Button>
                          }
                        />
                      </div>
                    </div>
                    <div className="border rounded-lg p-4">
                      <DocumentResultDisplay 
                        document={result} 
                        visualStyle={input.visualStyle?.theme}
                        imageAssets={input.imageAssets}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* エラー表示 (下部に移動) */}
          {error && !batchProgress && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>エラー</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </TabsContent>

        <TabsContent value="saved" className="space-y-4">
          <div className="space-y-4">
            {/* インポート・エクスポート機能 */}
            <div className="card">
              <div className="flex flex-col sm:flex-row gap-4">
                <DocumentImporter 
                  onImport={(importedDocs) => {
                    addBatchDocuments(importedDocs)
                  }}
                  maxCount={200}
                />
                <Button
                  variant="outline"
                  onClick={() => {
                    const dataStr = JSON.stringify(documents, null, 2)
                    const blob = new Blob([dataStr], { type: 'application/json' })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `documents_${Date.now()}.json`
                    document.body.appendChild(a)
                    a.click()
                    document.body.removeChild(a)
                    URL.revokeObjectURL(url)
                  }}
                  className="flex items-center gap-2"
                  disabled={documents.length === 0}
                >
                  <Download className="w-4 h-4" />
                  すべてエクスポート
                </Button>
              </div>
            </div>
            
            {/* 保存済み文献リスト */}
            <SavedDocumentsList
              onLoad={handleLoad}
              onContinue={handleContinue}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
